"use client";

/**
 * React Hook for App-Web Bridge
 * 用于在 Next.js 组件中使用 Bridge 功能
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  AppFeatures,
  AppInitData,
  MessageType,
  Platform,
  type AdjustAttributionData,
  type OsGetAdvertisingIdData,
  type AdjustTrackEventPayload,
  type AdjustTrackSubscriptionPayload,
  type AdjustUpdateSkanConversionValuePayload,
  type NativeStatusBarStyle,
} from "./types";
import { Bridge, getBridge } from "./bridge";

/**
 * Bridge Hook 返回值
 */
export interface UseBridgeReturn {
  bridge: Bridge;
  platform: Platform;
  isInApp: boolean;
  ready: boolean;
  send: (type: string, data?: any) => void;
  request: (
    type: string,
    data?: any,
    options?: { timeoutMs?: number; id?: string },
  ) => Promise<any>;
  requestDeviceInfo: () => Promise<any>;
  requestPushToken: () => Promise<string>;
  callMethod: (method: string, params?: any) => Promise<any>;
  /** 在 App 内上报关键事件（由原生 Firebase Analytics 上报，确保广告归因正常）；在 Web 内调用无效 */
  logAppEvent: (
    name: string,
    params?: Record<string, string | number | boolean>,
  ) => void;
  adjustTrackEvent: (
    payload: AdjustTrackEventPayload,
    options?: { timeoutMs?: number },
  ) => Promise<void>;
  adjustTrackSubscription: (
    payload: AdjustTrackSubscriptionPayload,
    options?: { timeoutMs?: number },
  ) => Promise<void>;
  adjustUpdateSkanConversionValue: (
    payload: AdjustUpdateSkanConversionValuePayload,
    options?: { timeoutMs?: number },
  ) => Promise<void>;
  adjustGetAdid: (options?: { timeoutMs?: number }) => Promise<string | null>;
  /** 仅 Android：GAID（`os-get-advertising-id`） */
  osGetAdvertisingId: (options?: {
    timeoutMs?: number;
  }) => Promise<OsGetAdvertisingIdData>;
  adjustGetAttribution: (options?: {
    timeoutMs?: number;
  }) => Promise<AdjustAttributionData>;
  adjustSetExternalDeviceId: (
    externalDeviceId: string,
    options?: { timeoutMs?: number },
  ) => Promise<void>;
  on: (type: string, callback: (data: any) => void) => () => void;
  /**
   * 跨平台打开外部链接（App 内置浏览器体验）：
   * - Android WebView：通过 bridge 发送 OPEN_URL_INAPP，由原生 Chrome Custom Tab 打开
   * - iOS / Web：window.open "_blank"，Safari View Controller 或新 Tab 打开
   */
  openUrl: (url: string) => void;
  /** App 内：请求原生二级 Detail 容器打开指定 https URL（container-control / open-detail） */
  openDetailInNative: (url: string) => void;
  /** App 内：关闭原生 Detail 二级容器（container-control / close-detail） */
  closeDetailInNative: () => void;
  /** App 内：设置原生二级 Detail 容器是否允许手势 / 系统返回关闭容器（container-control / set-detail-gesture-back） */
  setDetailGestureBack: (enabled: boolean) => void;
  /** App 内：设置当前前台 WebView 对应原生容器的状态栏样式（set-status-bar-style） */
  setStatusBarStyle: (style: NativeStatusBarStyle) => void;
}

/**
 * 使用 Bridge 的 React Hook
 */
export function useBridge(): UseBridgeReturn {
  const [bridge] = useState(() => getBridge());
  const [platform, setPlatform] = useState<Platform>(bridge.getPlatform());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // 等待 Bridge 初始化
    const timer = setTimeout(() => {
      setReady(true);
      setPlatform(bridge.getPlatform());
    }, 100);

    return () => clearTimeout(timer);
  }, [bridge]);

  const send = useCallback(
    (type: string, data?: any) => {
      bridge.send(type, data);
    },
    [bridge],
  );

  const request = useCallback(
    (
      type: string,
      data?: any,
      options?: { timeoutMs?: number; id?: string },
    ) => {
      return bridge.request(type, data, options);
    },
    [bridge],
  );

  const requestDeviceInfo = useCallback(() => {
    return bridge.requestDeviceInfo();
  }, [bridge]);

  const requestPushToken = useCallback(() => {
    return bridge.requestPushToken();
  }, [bridge]);

  const callMethod = useCallback(
    (method: string, params?: any) => {
      return bridge.callMethod(method, params);
    },
    [bridge],
  );

  const logAppEvent = useCallback(
    (name: string, params?: Record<string, string | number | boolean>) => {
      if (!bridge.isInApp()) {
        return;
      }
      bridge.send(MessageType.LOG_EVENT, { name, params });
    },
    [bridge],
  );

  const adjustTrackEvent = useCallback(
    (payload: AdjustTrackEventPayload, options?: { timeoutMs?: number }) => {
      return bridge.adjustTrackEvent(payload, options);
    },
    [bridge],
  );

  const adjustTrackSubscription = useCallback(
    (
      payload: AdjustTrackSubscriptionPayload,
      options?: { timeoutMs?: number },
    ) => {
      return bridge.adjustTrackSubscription(payload, options);
    },
    [bridge],
  );

  const adjustGetAdid = useCallback(
    (options?: { timeoutMs?: number }) => {
      return bridge.adjustGetAdid(options);
    },
    [bridge],
  );

  const osGetAdvertisingId = useCallback(
    (options?: { timeoutMs?: number }) => {
      return bridge.osGetAdvertisingId(options);
    },
    [bridge],
  );

  const adjustUpdateSkanConversionValue = useCallback(
    (
      payload: AdjustUpdateSkanConversionValuePayload,
      options?: { timeoutMs?: number },
    ) => {
      return bridge.adjustUpdateSkanConversionValue(payload, options);
    },
    [bridge],
  );

  const adjustGetAttribution = useCallback(
    (options?: { timeoutMs?: number }) => {
      return bridge.adjustGetAttribution(options);
    },
    [bridge],
  );

  const adjustSetExternalDeviceId = useCallback(
    (externalDeviceId: string, options?: { timeoutMs?: number }) => {
      return bridge.adjustSetExternalDeviceId(externalDeviceId, options);
    },
    [bridge],
  );

  /**
   * 跨平台打开链接（App 内置浏览器）。
   * - App 内：OPEN_URL_INAPP（Android Custom Tab / iOS SFSafariViewController）
   * - Web：window.open 新标签
   *
   * @param url 要打开的完整 URL
   */
  const openUrl = useCallback(
    (url: string) => {
      if (platform === "android" || platform === "ios") {
        bridge.send(MessageType.OPEN_URL_INAPP, { url });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [bridge, platform],
  );

  const openDetailInNative = useCallback(
    (url: string, options?: { gestureBackEnabled?: boolean }) => {
      bridge.openDetailInNative(url, options);
    },
    [bridge],
  );

  const setDetailGestureBack = useCallback(
    (enabled: boolean) => {
      bridge.setDetailGestureBack(enabled);
    },
    [bridge],
  );

  const setStatusBarStyle = useCallback(
    (style: NativeStatusBarStyle) => {
      bridge.setStatusBarStyle(style);
    },
    [bridge],
  );

  const closeDetailInNative = useCallback(() => {
    bridge.closeDetailInNative();
  }, [bridge]);

  const onUnsubscribeRef = useRef<Map<string, () => void>>(new Map());

  const on = useCallback(
    (type: string, callback: (data: any) => void) => {
      // 幂等：先解绑该 type 上之前通过本 hook 注册的监听，再注册新的，保证只有最后一次生效
      const prev = onUnsubscribeRef.current.get(type);
      if (prev) {
        prev();
        onUnsubscribeRef.current.delete(type);
      }
      const unsubscribe = bridge.on(type, callback);
      onUnsubscribeRef.current.set(type, unsubscribe);
      return () => {
        unsubscribe();
        if (onUnsubscribeRef.current.get(type) === unsubscribe) {
          onUnsubscribeRef.current.delete(type);
        }
      };
    },
    [bridge],
  );

  return {
    bridge,
    platform,
    isInApp: bridge.isInApp(),
    ready,
    send,
    request,
    requestDeviceInfo,
    requestPushToken,
    callMethod,
    logAppEvent,
    adjustTrackEvent,
    adjustTrackSubscription,
    adjustUpdateSkanConversionValue,
    adjustGetAdid,
    osGetAdvertisingId,
    adjustGetAttribution,
    adjustSetExternalDeviceId,
    on,
    openUrl,
    openDetailInNative,
    setDetailGestureBack,
    setStatusBarStyle,
    closeDetailInNative,
  };
}

/**
 * 获取 WebView 注入的初始化数据（比消息监听更快）
 * App 端在页面加载时会通过 JS 注入 window.__APP_DEVICE_INFO__
 */
function getInjectedAppInitData(): AppInitData | null {
  if (typeof window === "undefined") {
    return null;
  }

  const injectedData = (window as any).__APP_DEVICE_INFO__;
  if (!injectedData) {
    return null;
  }

  // 如果是完整的 app-init 格式（包含 platform, appVersion, deviceInfo 等）
  if (injectedData.platform && injectedData.deviceInfo) {
    return injectedData as AppInitData;
  }

  return null;
}

/**
 * 合并 App 注入的 features 与「壳内乐观默认」。
 *
 * 仅在 `immersiveNavbar` 未定义时，在可判定为 Edgen App 壳内时默认为 true，
 * 避免注入/Bridge 时序导致首帧按「不支持」渲染；若原生显式下发 false（如部分机型）则尊重。
 *
 * @param raw - 来自原生或 Bridge 的 features，可为空
 * @returns 合并后的 features，供根布局等使用
 */
function mergeFeaturesWithOptimistic(
  raw: AppFeatures | undefined | null,
): AppFeatures {
  const base: AppFeatures = raw ? { ...raw } : {};
  if (typeof base.immersiveNavbar === "boolean") {
    return base;
  }
  if (shouldUseOptimisticImmersiveNavbar()) {
    return { ...base, immersiveNavbar: true };
  }
  return base;
}

/**
 * 判断是否应在「immersiveNavbar 未定义」时采用乐观 true。
 *
 * 顺序：`Bridge.isInApp()` → UA 含 `EdgenApp/`（与原生 WebView 追加的版本串一致）→
 * `window.__APP_PLATFORM__` 为 ios/android（如 iOS 注入脚本设置）。
 * 用于 Bridge 尚未完成轮询、但已能确定在 App 壳内的首帧。
 */
function shouldUseOptimisticImmersiveNavbar(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    if (getBridge().isInApp()) {
      return true;
    }
  } catch {
    // ignore
  }
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (ua.includes("EdgenApp/")) {
    return true;
  }
  const plat = (window as { __APP_PLATFORM__?: string }).__APP_PLATFORM__;
  if (plat === "ios" || plat === "android") {
    return true;
  }
  return false;
}

/**
 * 解析当前应采用的原始 `features`（不做乐观合并）。
 *
 * 优先使用 `useAppInit` / Bridge `APP_INIT` 推送中的 `features`；
 * 若尚未有则用 `window.__APP_DEVICE_INFO__.features`（document start 注入）。
 *
 * @param appInitData - `useAppInit` 状态，可为 null
 */
function resolveRawFeatures(
  appInitData: AppInitData | null | undefined,
): AppFeatures | undefined {
  if (appInitData?.features !== undefined) {
    return appInitData.features;
  }
  if (typeof window !== "undefined") {
    const w = (window as { __APP_DEVICE_INFO__?: { features?: AppFeatures } })
      .__APP_DEVICE_INFO__?.features;
    if (w !== undefined && w !== null) {
      return w;
    }
  }
  return undefined;
}

/**
 * 计算当前时刻的完整 features 快照（原始值 + 乐观默认）。
 *
 * 供 `useAppFeatures` 初始 state、`useLayoutEffect` 重算及
 * `app-device-info-ready` / `safe-area-insets-changed` 纠偏时复用。
 *
 * @param appInitData - 与 resolveRawFeatures 入参含义相同
 */
function computeFeaturesSnapshot(
  appInitData: AppInitData | null | undefined,
): AppFeatures {
  return mergeFeaturesWithOptimistic(resolveRawFeatures(appInitData));
}

/**
 * 非 React 环境下读取原始 `features` 快照（语义同 `resolveRawFeatures`，不做乐观合并）。
 *
 * `resolveRawFeatures` 需要 `useAppInit` 的状态入参，异步工具函数（如付费墙 Callable）
 * 既拿不到该状态也不能调 Hook，因此提供这个零参版本。
 *
 * 两级回退不可省：`getInjectedAppInitData` 要求 platform 与 deviceInfo 同时存在才返回，
 * 而 Bridge 在 APP_INIT 时是整体 merge 回写 `__APP_DEVICE_INFO__`，存在 features 已到
 * 而其余字段未齐的时序；只走第一级会把「原生支持」误判成「老 App 不支持」。
 *
 * @returns 原始 features；App 尚未注入 / 浏览器环境下为 undefined
 */
export function resolveRawAppFeaturesSnapshot(): AppFeatures | undefined {
  return resolveRawFeatures(getInjectedAppInitData());
}

/**
 * 等到原生 `features` 就绪（或超时）后返回快照。
 *
 * 用于「挂载即弹窗」的路径（如 AlphaPick 详情页付费墙）：APP_INIT 可能比 Web 首帧慢几十毫秒，
 * 那一刻快照为 undefined，能力判定会退化成「老 App」从而闪现 Web 兜底 UI。
 * 非 App 环境（浏览器）立即返回，不引入任何等待。
 *
 * @param timeoutMs - 最长等待时间；超时按当前快照返回，绝不无限挂起
 */
export function waitForAppFeatures(
  timeoutMs = 300,
): Promise<AppFeatures | undefined> {
  const immediate = resolveRawAppFeaturesSnapshot();
  if (immediate !== undefined) {
    return Promise.resolve(immediate);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }
  let inApp = false;
  try {
    inApp = getBridge().isInApp();
  } catch {
    inApp = false;
  }
  if (!inApp) {
    return Promise.resolve(undefined);
  }

  return new Promise<AppFeatures | undefined>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      window.removeEventListener("app-device-info-ready", finish);
      resolve(resolveRawAppFeaturesSnapshot());
    };
    // Bridge 收到 APP_INIT 后会回写 __APP_DEVICE_INFO__ 并派发该事件（见 bridge.ts）
    window.addEventListener("app-device-info-ready", finish);
    timer = setTimeout(finish, timeoutMs);
  });
}

/**
 * 监听 App 初始化消息的 Hook
 * 优先使用 WebView 注入的值（更快），同时监听消息作为备用
 */
function useAppInit(callback?: (data: AppInitData) => void) {
  const { on, isInApp, requestDeviceInfo } = useBridge();
  // 初始化时直接检查注入值
  const [appInitData, setAppInitData] = useState<AppInitData | null>(() =>
    getInjectedAppInitData(),
  );

  // 如果初始就有注入值，触发 callback
  useEffect(() => {
    if (appInitData && callback) {
      callback(appInitData);
      setAppInitData(appInitData);
    }
    // 只在首次有注入数据时执行
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = on(MessageType.APP_INIT, (data: AppInitData) => {
      setAppInitData(data);
      if (callback) {
        callback(data);
      }
    });

    return unsubscribe;
  }, [on, callback]);

  useEffect(() => {
    if (!isInApp || appInitData) {
      return;
    }
    let cancelled = false;
    requestDeviceInfo()
      .then((data) => {
        if (cancelled || !data) {
          return;
        }
        setAppInitData(data);
        if (callback) {
          callback(data);
        }
      })
      .catch(() => {
        // ignore: keep bridge listener / injected data path as primary source
      });

    return () => {
      cancelled = true;
    };
  }, [appInitData, callback, isInApp, requestDeviceInfo]);

  return appInitData;
}

/**
 * 获取 App 功能特性支持标识的 Hook
 *
 * 用于判断当前 App 版本是否支持某些功能（如沉浸式导航栏）。
 * 在壳内且 `immersiveNavbar` 未定义时采用乐观默认（见 {@link mergeFeaturesWithOptimistic}），
 * 避免注入晚于首帧时误判；监听 `app-device-info-ready` 与 `safe-area-insets-changed` 做事件纠偏。
 * 纯 Web 或未命中乐观逻辑时行为与原先一致。
 *
 * @example
 * const features = useAppFeatures();
 * if (features.immersiveNavbar) {
 *   // 使用沉浸式导航栏
 * }
 */
export function useAppFeatures(): AppFeatures {
  const appInitData = useAppInit();
  const [features, setFeatures] = useState<AppFeatures>(() => {
    if (typeof window === "undefined") {
      return {};
    }
    return computeFeaturesSnapshot(getInjectedAppInitData());
  });

  const recompute = useCallback(() => {
    setFeatures(computeFeaturesSnapshot(appInitData ?? null));
  }, [appInitData]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const onCorrection = () => {
      recompute();
    };
    window.addEventListener("app-device-info-ready", onCorrection);
    window.addEventListener("safe-area-insets-changed", onCorrection);
    return () => {
      window.removeEventListener("app-device-info-ready", onCorrection);
      window.removeEventListener("safe-area-insets-changed", onCorrection);
    };
  }, [recompute]);

  return features;
}
